///
/// EarthBrowser
///
/// Created by Matt Giger on 10/28/2026
/// Copyright (c) 2026 Geodata Labs
///

import type { EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { wgs } from "@/earthbrowser/utils/geographic"
import { GestureState } from "@stovokor/gestures/GestureRecognizer"
import { TapDragGestureRecognizer, type TapDragGestureEventData } from "@stovokor/gestures/TapDragGestureRecognizer"
import type { EarthCameraState, GroundDragAnchor } from "../EarthCameraState"

const cZoomSpringDamping = 15
// Vertical travel that halves (drag down) or doubles (drag up) the altitude,
// at the reference viewport height. Phone map apps land around a quarter of
// the screen per doubling
const cPixelsPerDoubling = 180
const cReferenceViewportHeight = 1000

/**
 * EarthTapDragZoomHandler - tap, then press and drag to zoom (one-handed
 * zoom on phones; double-click-and-drag with a mouse).
 *
 * Drag down zooms in, up zooms out, exponentially in the travel, about the
 * ground under the second touch (EarthCameraState.zoomAboutScreenPoint), so
 * the place you tapped stays put. Targets go through the zoom spring like
 * every other zoom. A second tap that lifts without dragging is left to the
 * double-tap recognizer.
 */
export class EarthTapDragZoomHandler extends TapDragGestureRecognizer {
	private context: EarthBrowserContext
	private cameraState: EarthCameraState
	private anchor?: GroundDragAnchor
	private startAltitude = 0

	constructor(context: EarthBrowserContext, cameraState: EarthCameraState) {
		super({
			maxTapDuration: 300,
			maxTapMovement: 20,
			maxIntervalBetweenTaps: 350,
			// Below the pan's 5 px so this begins (and so owns the touch) first
			minimumMovement: 3
		})
		this.context = context
		this.cameraState = cameraState
		// Above the pan (10) for the frame where both cross their thresholds
		this.priority = 15

		this.onGesture((data: TapDragGestureEventData) => {
			switch (data.state) {
				case GestureState.Began:
					this.start(data)
					break
				case GestureState.Changed:
					this.update(data)
					break
				case GestureState.Ended:
				case GestureState.Cancelled:
					this.finish()
					break
			}
		})
	}

	private start(data: TapDragGestureEventData): void {
		if (!this.owner) return
		this.context.focusManager.requestFocus(this.owner, { preserveCurrentState: true })
		this.cameraState.syncFromCamera()
		this.anchor = this.cameraState.captureGroundDragAnchor(data.startX, data.startY)
		this.startAltitude = this.cameraState.spherical.radius - wgs.a
		this.update(data)
	}

	private update(data: TapDragGestureEventData): void {
		const anchor = this.anchor
		if (!anchor) return
		const height = this.cameraState.camera?.projection.height ?? 0
		const scale = height > 0 ? cReferenceViewportHeight / height : 1
		const doublings = -data.translationY * scale / cPixelsPerDoubling
		const altitude = this.startAltitude * Math.pow(2, doublings)
		const target = this.cameraState.zoomAboutScreenPoint(anchor, data.startX, data.startY, wgs.a + altitude)
		if (target) this.cameraState.animateTo(target, cZoomSpringDamping, "user")
	}

	private finish(): void {
		this.anchor = undefined
		this.cameraState.saveCameraState()
	}

	override reset(): void {
		super.reset()
		this.anchor = undefined
		this.startAltitude = 0
	}
}
